import { Gem } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ProductPicker } from '@/features/resource-pickers';

import { useChatDiscounts } from '../hooks/use-chat-discounts';
import {
  useCreateChatDraftOrder,
  useIssueDiscount,
  useSendChatInvoice,
  useSendMessage,
} from '../hooks/use-conversation-mutations';

export interface JewelleryActionsProps {
  storeId: string;
  conversationId: string;
  /** The conversation's latest quote, if staff built one from it. */
  draftOrderId: string | null;
  disabled: boolean;
}

/**
 * The composer's jewellery cards (LC12): a product, the size guide, a single-use discount code
 * (capped per member per day), and a draft order quote opened in a new tab, then its invoice.
 */
export function JewelleryActions({
  storeId,
  conversationId,
  draftOrderId,
  disabled,
}: JewelleryActionsProps) {
  const { t } = useTranslation('conversations');
  const format = useFormat();
  const { can } = usePermissions();
  const [picking, setPicking] = useState(false);
  const [discountsOpen, setDiscountsOpen] = useState(false);
  const send = useSendMessage(storeId, conversationId);
  const discounts = useChatDiscounts(storeId, discountsOpen);
  const issue = useIssueDiscount(storeId, conversationId);
  const draft = useCreateChatDraftOrder(storeId, conversationId);
  const invoice = useSendChatInvoice(storeId, conversationId);
  const canQuote = can('orders.write');

  const newQuote = () => {
    draft.mutate(
      { includeProducts: true },
      {
        onSuccess: (created) => {
          window.open(`/s/${storeId}/draft-orders/${created.id}`, '_blank', 'noopener');
          if (created.skipped.length > 0) {
            toast.info(t('jewellery.quoteSkipped', { count: created.skipped.length }));
          } else {
            toast.success(t('jewellery.quoteCreated', { name: created.name }));
          }
        },
      },
    );
  };

  const allowance = discounts.data;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t('jewellery.menu')}
            title={t('jewellery.menu')}
            disabled={disabled}
          >
            <Gem aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => setPicking(true)}>
            {t('jewellery.sendProduct')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => send.mutate({ kind: 'size_guide', payload: {} })}>
            {t('jewellery.sendSizeGuide')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDiscountsOpen(true)}>
            {t('jewellery.giveDiscount')}
          </DropdownMenuItem>
          {canQuote ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={newQuote} disabled={draft.isPending}>
                {t('jewellery.newQuote')}
              </DropdownMenuItem>
              {draftOrderId ? (
                <>
                  <DropdownMenuItem
                    onSelect={() =>
                      window.open(
                        `/s/${storeId}/draft-orders/${draftOrderId}`,
                        '_blank',
                        'noopener',
                      )
                    }
                  >
                    {t('jewellery.openQuote')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => invoice.mutate()} disabled={invoice.isPending}>
                    {t('jewellery.sendInvoice')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <ProductPicker
        open={picking}
        onOpenChange={setPicking}
        scope={{ kind: 'store', storeId }}
        title={t('jewellery.sendProduct')}
        confirmLabel={t('jewellery.sendProductConfirm')}
        statuses={['published']}
        multiple={5}
        onConfirm={(selection) => {
          for (const productId of selection.ids) {
            send.mutate({ kind: 'product', payload: { productId } });
          }
          setPicking(false);
        }}
      />

      <Dialog open={discountsOpen} onOpenChange={setDiscountsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('jewellery.giveDiscount')}</DialogTitle>
            <DialogDescription>
              {allowance
                ? t('jewellery.allowance', {
                    remaining: allowance.remaining,
                    cap: allowance.cap,
                    resetsAt: format.dateTime(allowance.resetsAt),
                  })
                : t('jewellery.loadingDiscounts')}
            </DialogDescription>
          </DialogHeader>
          {allowance?.items.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('jewellery.noDiscounts')}</p>
          ) : null}
          <ul className="space-y-1" aria-label={t('jewellery.giveDiscount')}>
            {allowance?.items.map((discount) => (
              <li key={discount.id}>
                <button
                  type="button"
                  disabled={allowance.remaining === 0 || issue.isPending}
                  className="hover:bg-accent flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm disabled:opacity-50"
                  onClick={() =>
                    issue.mutate(discount.id, { onSuccess: () => setDiscountsOpen(false) })
                  }
                >
                  <span>{discount.title}</span>
                  <span className="text-muted-foreground text-xs">{discount.summary}</span>
                </button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
